import { FormikProps, useFormik } from "formik";
import React, { createContext, useContext, useState } from "react";
import * as Yup from "yup";
import {
  createBlog as onCreateBlog,
  updateBlog as onUpdateBlog,
} from "slices/thunk";
import { objectUrlToBlob } from "utils/file";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

export interface SubSection {
  mediaId?: string;
  title?: string;
  type: string;
  attrs: Record<string, any>;
  isEditing: boolean;
  order: number;
}

interface Section {
  title: string;
  body: SubSection[];
  isEditing: boolean;
  order: number;
}

interface AuthorId {
  // _id: string;
  name: string;
  email: string;
  image: string;
}

interface FormData {
  authorId: AuthorId;
  title: string;
  slug: string;
  sections: Section[];
  coverImage: string;
  categories: string[];
  tags: string[];
  status: "draft" | "published" | "archived";
}

export interface BlogFormValues {
  authorId: AuthorId;
  title: string;
  slug: string;
  sections: Section[];
  coverImage: string;
  categories: string[];
  tags: string[];
  status: "draft" | "published" | "archived";
}

interface MediaItem {
  path: string;
  preview: string;
  file: File | Blob;
  blobName: string;
  type: string;
}

type BlogMedia = {
  coverImage: MediaItem;
  [idKey: string]: MediaItem; // Allow dynamic keys
};

interface FormContextType {
  updateFormData: (newData: Partial<FormData>) => void;
  blogValidation: FormikProps<BlogFormValues>;
  selectedSectionOrder: number;
  setSelectedSectionOrder: (value: number) => void;
  getNextOrder: (dataArray: { order: number }[]) => number;
  setBlogInfo: React.Dispatch<React.SetStateAction<FormData>>;
  blogInfo: FormData;
  handleSaveSection: (sectionData: Section) => void;
  handlePublishBlog: () => void;
  isBlogEditing: boolean;
  setIsBlogEditing: (value: boolean) => void;
  deletedSubSectionIds: string[];
  setDeletedSubSectionIds: (value: React.SetStateAction<string[]>) => void;
  deletedSectionIds: string[];
  setDeletedSectionIds: (value: React.SetStateAction<string[]>) => void;
  deletedMedia: string[];
  setDeletedMedia: (value: string[]) => void;
  setBlogMedia: (value: React.SetStateAction<BlogMedia>) => void;
  blogMedia: BlogMedia;
}

const initialFormData: FormData = {
  authorId: { name: "", email: "", image: "" },
  title: "",
  slug: "",
  sections: [
    {
      title: "",
      body: [
        {
          mediaId: "",
          type: "",
          title: "",
          attrs: {},
          isEditing: true,
          order: 1,
        },
      ],
      isEditing: true,
      order: 1,
    },
  ],
  coverImage: "",
  categories: [],
  tags: [],
  status: "published",
};

const FormContext = createContext<FormContextType | undefined>(undefined);

export const FormProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedSectionOrder, setSelectedSectionOrder] = useState<number>(1);
  const [blogInfo, setBlogInfo] = useState<BlogFormValues>(initialFormData);
  const [isBlogEditing, setIsBlogEditing] = useState<boolean>(false);
  const [deletedSubSectionIds, setDeletedSubSectionIds] = useState<string[]>(
    []
  );
  const [deletedSectionIds, setDeletedSectionIds] = useState<string[]>([]);
  const [deletedMedia, setDeletedMedia] = useState<string[]>([]);
  const [blogMedia, setBlogMedia] = useState<BlogMedia>({} as BlogMedia);

  const { blog } = useSelector((state: any) => state.Blog);
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const blogValidation: any = useFormik<BlogFormValues>({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: isBlogEditing ? blog : blogInfo,
    validationSchema: Yup.object({
      title: Yup.string().required("Blog title is required"),
      sections: Yup.array().of(
        Yup.object({
          title: Yup.string().required("Section title is required"),
          body: Yup.array().of(
            Yup.object({
              type: Yup.string().required("Type is required"),
              attrs: Yup.object().required("Attributes are required"),
            })
          ),
        })
      ),
      coverImage: Yup.string().required("CoverImage is required"),
      categories: Yup.array().of(Yup.string().required("Category is required")),
      tags: Yup.array().of(Yup.string().required("Tag is required")),
      status: Yup.string()
        .oneOf(["draft", "published", "archived"], "Invalid status")
        .required("Status is required"),
    }),
    onSubmit: async (values) => {
      const formData: any = new FormData();

      // let blogDetails = { ...values };
      let blogDetails = JSON.parse(JSON.stringify(values));

      if (isBlogEditing) {
        // Filter out deleted subsections
        blogDetails.authorId = blogDetails.authorId._id;
        blogDetails.sections = blogDetails.sections
          .filter((section: any) => !deletedSectionIds.includes(section._id))
          .map((section: Section, sectionIndex: number) => ({
            ...section,
            body: section.body
              .filter(
                (subSection: any) =>
                  !deletedSubSectionIds.includes(subSection._id)
              )
              .map((subSection: any, index: number) => ({
                ...subSection,
                order: index + 1, // 🔥 Reassign sequential order
              })),
            order: sectionIndex + 1,
          }));
        // Process images and videos inside sections
        blogDetails.sections = await Promise.all(
          blogDetails.sections.map(async (section: Section) => {
            section.body = await Promise.all(
              section.body.map(async (subSection, index) => {
                // subSection.order = index + 1;
                if (
                  subSection.type === "image" &&
                  subSection.attrs.src.startsWith("blob")
                ) {
                  // Upload new image only if it's a new blob URL
                  // const blob = await objectUrlToBlob(subSection.attrs.src);
                  // const key = `images${section.order}ss${subSection.order}`;
                  // formData.append(key, blob);
                  // const key = `images${section.order}ss${subSection.order}`;
                  // const file = blogMedia[key];

                  const idKey = subSection.mediaId;
                  const file = blogMedia[idKey as string];

                  // const key = `coverImage`;
                  const blob = new Blob([file?.file], {
                    type: file?.type,
                  });
                  const fileWithName = new File([blob], file?.blobName, {
                    type: file?.type,
                  });
                  formData.append(idKey, fileWithName);
                  subSection.attrs.src = idKey;
                }

                if (
                  subSection.type === "video" &&
                  subSection.attrs.src.startsWith("blob")
                ) {
                  // Upload new video only if it's a new blob URL
                  // const blob = await objectUrlToBlob(subSection.attrs.src);
                  // const key = `videos${section.order}ss${subSection.order}`;
                  // formData.append(key, blob);
                  // const key = `videos${section.order}ss${subSection.order}`;
                  // const file = blogMedia[key];
                  const idKey = subSection.mediaId;
                  const file = blogMedia[idKey as string];
                  // const key = `coverImage`;
                  const blob = new Blob([file?.file], {
                    type: file?.type,
                  });
                  const fileWithName = new File([blob], file.blobName, {
                    type: file?.type,
                  });
                  formData.append(idKey, fileWithName);
                  subSection.attrs.src = idKey;
                }

                return subSection;
              })
            );
            return section;
          })
        );

        // Handle cover image (only if updated)
        if (blogDetails.coverImage.startsWith("blob")) {
          // const blob = await objectUrlToBlob(blogDetails.coverImage);
          // const key = `coverImage`;
          // formData.append(key, blob);
          const file = blogMedia.coverImage;
          const key = `coverImage`;

          const blob = new Blob([file.file], {
            type: file.type,
          });
          const fileWithName = new File([blob], file.blobName, {
            type: file?.type,
          });
          formData.append(key, fileWithName);
          blogDetails.coverImage = key;
        }

        formData.append(
          "deletedSubSectionsIds",
          JSON.stringify(deletedSubSectionIds)
        );
        formData.append(
          "deletedSectionsIds",
          JSON.stringify(deletedSectionIds)
        );
        formData.append("deletedMediaUrls", JSON.stringify(deletedMedia));
        formData.append("details", JSON.stringify(blogDetails));
        try {
         const response = await dispatch(onUpdateBlog(blogDetails._id, formData, navigate));
          if (response) {
            dispatch(setBlogInfo({} as BlogFormValues));
            dispatch(setBlogMedia({} as BlogMedia));
          }
          // navigate("/blogs");
        } catch (error) {
          console.log(error);
        }
      } else {
        blogDetails.sections = await Promise.all(
          blogDetails.sections.map(async (section: Section) => {
            section.body = await Promise.all(
              section.body.map(async (subSection, index) => {
                subSection.order = index + 1;
                if (subSection.type === "image") {
                  // const blob = await objectUrlToBlob(subSection.attrs.src);
                  // const file = blogMedia.key;
                  const idKey = subSection.mediaId;
                  const file = blogMedia[idKey as string];
                  // const key = `coverImage`;
                  const blob = new Blob([file?.file], {
                    type: file?.type,
                  });
                  const fileWithName = new File([blob], file.blobName, {
                    type: file?.type,
                  });
                  formData.append(idKey, fileWithName);
                  subSection.attrs.src = idKey;
                  // Do not include id in the returned subSection
                  const { mediaId, ...subSectionWithoutId } = subSection; // Exclude id
                  return subSectionWithoutId;
                }
                if (subSection.type === "video") {
                  // const blob = await objectUrlToBlob(subSection.attrs.src);
                  const idKey = subSection.mediaId;
                  const file = blogMedia[idKey as string];
                  // const key = `coverImage`;
                  const blob = new Blob([file?.file], {
                    type: file?.type,
                  });
                  const fileWithName = new File([blob], file.blobName, {
                    type: file?.type,
                  });
                  formData.append(idKey, fileWithName);
                  subSection.attrs.src = idKey;
                  const { mediaId, ...subSectionWithoutId } = subSection; // Exclude id
                  return subSectionWithoutId;
                }
                return subSection;
              })
            );
            return section;
          })
        );

        if (blogDetails.coverImage.startsWith("blob")) {
          const file = blogMedia.coverImage;
          const key = `coverImage`;

          const blob = new Blob([file.file], {
            type: file.type,
          });
          const fileWithName = new File([blob], file.blobName, {
            type: file?.type,
          });
          formData.append(key, fileWithName);
          blogDetails.coverImage = key;
        }
        formData.append("details", JSON.stringify(blogDetails));
        try {
          const response = await dispatch(onCreateBlog(formData, navigate));
          if (response) {
            dispatch(setBlogInfo({} as BlogFormValues));
            dispatch(setBlogMedia({} as BlogMedia));
          }
        } catch (error) {
          console.log(error);
        }
      }
    },
  });

  // const handleSaveSection = (sectionData: Section) => {
  //   const clonedSections = [...blogValidation.values.sections];
  //   const sectionIndex = clonedSections.findIndex(
  //     (section) => section.order === sectionData.order
  //   );
  //   // clonedSections[sectionIndex] = sectionData;
  //   clonedSections[sectionIndex] = {
  //     ...sectionData,
  //     isEditing: false, // Preserve `isEditing`
  //   };
  //   blogValidation.setFieldValue("sections", clonedSections);
  //   setBlogInfo((prevBlogInfo) => ({
  //     ...prevBlogInfo,
  //     sections: clonedSections,
  //   }));
  //   setSelectedSectionOrder(sectionData.order);
  // };

  const handleSaveSection = (sectionData: Section) => {
    // Remove deleted sections
    const updatedSectionsWithIsEditing = blogInfo.sections.filter(
      (section) => section.body.length > 0
    );

    const updatedSectionsWithoutIsEditing =
      blogValidation.values.sections.filter(
        (section: Section) => section.body.length > 0
      );

    const sectionIndex = updatedSectionsWithIsEditing.findIndex(
      (section) => section.order === sectionData.order
    );

    if (sectionIndex !== -1) {
      // Update section while preserving `isEditing`
      updatedSectionsWithIsEditing[sectionIndex] = {
        ...sectionData,
        isEditing: false,
      };

      // Update validation state without `isEditing`
      const { isEditing, ...sectionWithoutEditing } = sectionData;
      updatedSectionsWithoutIsEditing[sectionIndex] = sectionWithoutEditing;
    }

    // Update Formik validation state
    blogValidation.setFieldValue("sections", updatedSectionsWithoutIsEditing);

    // Update blog state
    setBlogInfo((prevBlogInfo) => ({
      ...prevBlogInfo,
      sections: updatedSectionsWithIsEditing,
    }));

    setSelectedSectionOrder(sectionData.order);
  };

  function getNextOrder(dataArray: { order: number }[]) {
    if (!Array.isArray(dataArray)) {
      throw new Error("Input must be an array");
    }

    // Extract the maximum order value from the array
    const maxOrder = dataArray.reduce((max, item) => {
      return item.order > max ? item.order : max;
    }, 0);

    // Return the next order value
    return maxOrder + 1;
  }

  const updateFormData = (newData: Partial<FormData>) => {
    setBlogInfo((prev) => ({ ...prev, ...newData }));
  };

  return (
    <FormContext.Provider
      value={{
        setBlogInfo,
        blogInfo,
        updateFormData,
        blogValidation,
        selectedSectionOrder,
        setSelectedSectionOrder,
        getNextOrder,
        handleSaveSection,
        handlePublishBlog: blogValidation.handleSubmit,
        isBlogEditing,
        setIsBlogEditing,
        deletedSubSectionIds,
        setDeletedSubSectionIds,
        setDeletedSectionIds,
        deletedSectionIds,
        deletedMedia,
        setDeletedMedia,
        setBlogMedia,
        blogMedia,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = (): FormContextType => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
};
